import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * Состояние первого входа: телефон и согласие, карточка ребёнка, направление.
 * Живёт до конца онбординга — шаги переходят по маршрутам, а данные собираются
 * здесь и уходят на сервер в те моменты, когда для этого есть эндпоинт.
 */

export const UNSURE = 0;

export type Onboarding = {
  phone: string;
  phoneFromTelegram: boolean;
  consent: boolean;
  name: string;
  age: string;
  note: string;
  /** id направления, `UNSURE` — «пока не знаю», null — ещё не выбрано. */
  directionId: number | null;
  specialistId: number | null;
  /** Ребёнок, заведённый на шаге 2: дальше он нужен и для заявки, и для записи. */
  childId: number | null;
  /** Кому ушла заявка — показываем это на последнем экране. */
  sentToName: string | null;
};

const EMPTY: Onboarding = {
  phone: '',
  phoneFromTelegram: false,
  consent: false,
  name: '',
  age: '',
  note: '',
  directionId: null,
  specialistId: null,
  childId: null,
  sentToName: null,
};

type Api = {
  nb: Onboarding;
  patch: (p: Partial<Onboarding>) => void;
  reset: () => void;
};

const Ctx = createContext<Api | null>(null);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [nb, setNb] = useState<Onboarding>(EMPTY);
  const patch = useCallback((p: Partial<Onboarding>) => setNb((prev) => ({ ...prev, ...p })), []);
  const reset = useCallback(() => setNb(EMPTY), []);
  const value = useMemo(() => ({ nb, patch, reset }), [nb, patch, reset]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOnboarding(): Api {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('OnboardingProvider отсутствует выше по дереву');
  return ctx;
}
